import React, { useEffect, useMemo } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import {
    PoliciesListType,
    PolicyReassignmentReason,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { PopupWrapperElement } from "@front-shared/elements/popupWrapper.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { SelectElement } from "@front-shared/elements/select.element.tsx"
import { useStateCallback } from "@front-shared/hooks/useStateCallback.hook.ts"
import type { IPopupProps } from "@front-shared/types/props.types.ts"

import { POLICIES_CONFIG } from "../../config/policies.config"
import { RuntimeHelper } from "../../helpers/runtime.helper"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook.ts"
import { usePoliciesList } from "../../hooks/policiesList.hook.ts"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { taskAssignmentsBulkReassign } from "../../store/thunks/taskAssignments.thunks.ts"
import { IAssignmentIdentification } from "../../types/objects.types.ts"

import closeIcon from "@front-shared/assets/images/icons/close-dark.svg"

export const TaskAssignmentUnassignPopupComponent: React.FunctionComponent<
    ITaskAssignmentUnassignPopupComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const { onSuccessReloadList: onSuccessReloadListShared } = usePoliciesList()

    const hasReassignmentReasonFeatureFlag = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_TASKS_REASSIGNMENT_REASON,
    )

    const [selectedReassignmentReason, setSelectedReassignmentReason] =
        useStateCallback<PolicyReassignmentReason | null>(null)

    const isReAssigning = useMemo<boolean>(() => {
        return RuntimeHelper.isTaskAssignmentsBulkReassignLoading()
    }, [runtimeState.isLoading])

    const coverageChecksToProcess = useMemo(() => {
        return props.coverageChecks.filter(coverageCheck => {
            return (
                coverageCheck.policy?.latestAssignment &&
                POLICIES_CONFIG.activeAssignmentStatuses.includes(
                    coverageCheck.policy.latestAssignment.status,
                )
            )
        })
    }, [props.coverageChecks])

    const ignoredCoverageChecksCount = useMemo(() => {
        return props.coverageChecks.length - coverageChecksToProcess.length
    }, [props.coverageChecks, coverageChecksToProcess])

    // Just a safe guard, close popup if no coverage checks to process
    useEffect(() => {
        if (!coverageChecksToProcess.length) {
            props.onDone()
        }
    }, [coverageChecksToProcess])

    const onSuccessReloadList = (
        results: (IOneVerifyCoverageCheckHistory | undefined)[],
    ) => {
        onSuccessReloadListShared(props.policiesListType, results)

        props.onDone()
    }

    const onUnassign = () => {
        if (
            !selectedPracticeRole ||
            (!!hasReassignmentReasonFeatureFlag && !selectedReassignmentReason)
        ) {
            return
        }

        const assignments: IAssignmentIdentification[] =
            coverageChecksToProcess.map(item => {
                return {
                    id: item.policy!.latestAssignment!.id,
                    nirvanaRequestId: item.coverageResult.resultId,
                }
            })

        dispatch(
            taskAssignmentsBulkReassign({
                practice: selectedPracticeRole.practice,
                payload: {
                    assignments,
                    reassignmentReason: selectedReassignmentReason || undefined,
                },
                onSuccess: onSuccessReloadList,
            }),
        )
    }

    return (
        <PopupWrapperElement
            isActive={props.isActive}
            className={`
                ${props.className}
            `}
            onDone={props.onDone}
            closeOnOutsideClick={props.closeOnOutsideClick}
            popupWidthClassName="w-[550px] p-[24px]! md:w-screen md:h-screen"
        >
            <div
                className="p-[8px] absolute top-[16px] right-[16px] cursor-pointer"
                onClick={props.onDone}
            >
                <img src={closeIcon} alt="Close" title="Close" />
            </div>

            <PrimaryText typography="h4" className="flex-1">
                Unassign {coverageChecksToProcess.length}{" "}
                {coverageChecksToProcess.length > 1 ? `policies` : `policy`}
            </PrimaryText>

            {ignoredCoverageChecksCount ? (
                <div className="mt-[16px] bg-brand-lilacLight rounded-[4px] p-[12px]">
                    <PrimaryText size={14} lineHeight={21}>
                        <span className="text-bold">
                            {ignoredCoverageChecksCount} unassigned{" "}
                            {ignoredCoverageChecksCount > 1
                                ? `policies`
                                : `policy`}
                        </span>{" "}
                        {ignoredCoverageChecksCount > 1 ? `were` : `was`}{" "}
                        removed from your selection, as there are currently no
                        individuals assigned to{" "}
                        {ignoredCoverageChecksCount > 1 ? `them` : `it`}.
                    </PrimaryText>
                </div>
            ) : null}

            <PrimaryText className="mt-[16px]">
                Are you sure you want to unassign these policies? This will
                cancel the current assignments.
            </PrimaryText>

            {!!hasReassignmentReasonFeatureFlag && (
                <>
                    <PrimaryText className="mt-[24px]">
                        Select the reason you are removing the assignments.
                    </PrimaryText>

                    <SelectElement
                        name="reassignmentReasonSelect"
                        className="mt-[16px]"
                        placeholder="Select a reason"
                        renderedOptions={
                            POLICIES_CONFIG.policyReassignmentReasonsRendered
                        }
                        onSelected={(reason: PolicyReassignmentReason) => {
                            setSelectedReassignmentReason(reason)
                        }}
                        selectClassName="deep-purple-border"
                        size="large"
                    />
                </>
            )}

            <div className="flex items-center justify-between mt-[16px]">
                <ButtonElement
                    label="Never mind"
                    type="default"
                    size="large"
                    htmlType="button"
                    onClick={props.onDone}
                />

                <ButtonElement
                    label="Unassign and remove"
                    type="primary"
                    size="large"
                    htmlType="button"
                    onClick={onUnassign}
                    disabled={
                        !!hasReassignmentReasonFeatureFlag &&
                        !selectedReassignmentReason
                    }
                    isLoading={isReAssigning}
                />
            </div>
        </PopupWrapperElement>
    )
}

interface ITaskAssignmentUnassignPopupComponentProps extends IPopupProps {
    coverageChecks: IOneVerifyCoverageCheckHistory[]
    policiesListType: PoliciesListType
}
